import React, { Children, useMemo } from 'react';
import Select, {
  components,
  CSSObjectWithLabel,
  GroupBase,
  MultiValue,
  OptionProps,
  StylesConfig,
  ValueContainerProps,
} from 'react-select';
import { useTheme } from '@mui/material';
import CheckboxComponent from 'app/components/Checkbox/Checkbox';
import {
  CustomSelectContainer,
  CustomSelectStyle,
  InputLabelContainer,
  TextFieldArea,
} from 'app/components/CustomSelect/styles';
import { TCustomValue } from 'app/components/CustomSelect';
import { CheckboxOptionContent, SelectedCountLabel } from './styles';

type AmortizationGraceMonthsSelectProps = {
  labelValue: string;
  name: string;
  options: TCustomValue[];
  value: TCustomValue[];
  placeholder?: string;
  className?: string;
  handleInputChange: (evt: {
    target: { name: string; value?: TCustomValue[] };
  }) => void;
};

const AmortizationGraceMonthsSelect = ({
  labelValue,
  name,
  options,
  value,
  placeholder = 'Selecionar',
  className,
  handleInputChange,
}: AmortizationGraceMonthsSelectProps) => {
  const theme = useTheme();
  const primaryColor = theme.palette.brand.primary.base;

  const CheckboxOption = (props: OptionProps<TCustomValue, true>) => (
    <components.Option {...props}>
      <CheckboxOptionContent>
        <CheckboxComponent
          checked={props.isSelected}
          customcolor={primaryColor}
          size="small"
          tabIndex={-1}
          disableRipple
          sx={{
            padding: 0,
            pointerEvents: 'none',
            color: primaryColor,
            '&.Mui-checked': {
              color: primaryColor,
            },
          }}
        />
        <span>{props.label}</span>
      </CheckboxOptionContent>
    </components.Option>
  );

  const ValueContainer = ({
    children,
    ...props
  }: ValueContainerProps<TCustomValue, true>) => {
    const selected = props.getValue();
    const [, input] = Children.toArray(children);

    return (
      <components.ValueContainer {...props}>
        {selected.length > 0 ? (
          <>
            <SelectedCountLabel>
              {selected.length === 1
                ? selected[0].label
                : `${selected.length} parcelas selecionadas`}
            </SelectedCountLabel>
            {input}
          </>
        ) : (
          children
        )}
      </components.ValueContainer>
    );
  };

  const customSelectStyle = useMemo((): StylesConfig<
    TCustomValue,
    true,
    GroupBase<TCustomValue>
  > => {
    const base = CustomSelectStyle(theme) as StylesConfig<
      TCustomValue,
      true,
      GroupBase<TCustomValue>
    >;

    return {
      ...base,
      control: (provided, state) =>
        ({
          ...(typeof base.control === 'function'
            ? base.control(provided, state)
            : provided),
          height: '2.5rem',
          minHeight: '2.5rem',
        } as CSSObjectWithLabel),
      option: (provided, state) =>
        ({
          ...provided,
          backgroundColor: state.isFocused
            ? theme.palette.brand.gray[100] ?? '#F5F5F5'
            : 'white',
          color: theme.palette.text.primary,
          cursor: 'pointer',
          padding: '8px 12px',
          '&:active': {
            backgroundColor: theme.palette.brand.gray[100] ?? '#F5F5F5',
          },
        } as CSSObjectWithLabel),
      multiValue: () => ({ display: 'none' }),
      valueContainer: (provided) =>
        ({
          ...provided,
          flexWrap: 'nowrap',
          overflow: 'hidden',
          paddingTop: 0,
          paddingBottom: 0,
        } as CSSObjectWithLabel),
    };
  }, [theme]);

  return (
    <CustomSelectContainer>
      <InputLabelContainer>{labelValue}</InputLabelContainer>
      <TextFieldArea hasLabel>
        <Select<TCustomValue, true>
          inputId={name}
          className={className}
          classNames={{
            control: () => 'select-control',
            valueContainer: () => 'value-container',
          }}
          value={value}
          options={options}
          placeholder={placeholder}
          isMulti
          isClearable={false}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          blurInputOnSelect={false}
          styles={customSelectStyle}
          components={{
            Option: CheckboxOption,
            MultiValue: () => null,
            ValueContainer,
          }}
          onChange={(selected: MultiValue<TCustomValue>) =>
            handleInputChange({
              target: {
                name,
                value: selected ? [...selected] : [],
              },
            })
          }
        />
      </TextFieldArea>
    </CustomSelectContainer>
  );
};

export default AmortizationGraceMonthsSelect;
